import { stringToColor } from "../Orders/helpers";

/**
 * Groups an array of people by their team.
 * 
 * @param {Array} people - The array of people to group.
 * @param {String} teamKey - The key used to identify the team (default is "kpi_team").
 * @returns {Object} - An object where keys are team names and values are arrays of people in each team.
 */
export function groupByTeam(people, teamKey = "kpi_team") {
    return people.reduce((acc, person) => {
        const team = person[teamKey]; // Access the team property dynamically
        if (!acc[team]) {
            acc[team] = []; // Initialize an empty array for the team if it doesn't exist
        }
        acc[team].push(person); // Add the person to the corresponding team array
        return acc;
    }, {});
}

export const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

export const shortCurrencyFormatter = (value) => {
    const absValue = Math.abs(value);
    let formatted;

    if (absValue < 1e3) {
        // Value is less than 1,000: use full currency formatting with one decimal place.
        formatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        }).format(value);
    } else if (absValue < 1e6) {
        // Value is in thousands: format as "$X.XK".
        const rounded = Math.round((value / 1e3) * 10) / 10;
        formatted = '$' + new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        }).format(rounded) + 'K';
    } else if (absValue < 1e9) {
        // Value is in millions: format as "$X.XM".
        const rounded = Math.round((value / 1e6) * 10) / 10;
        formatted = '$' + new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        }).format(rounded) + 'M';
    } else {
        // Value is in billions/trillions: format as "$X.XT".
        const rounded = Math.round((value / 1e9) * 10) / 10;
        formatted = '$' + new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        }).format(rounded) + 'T';
    }

    return formatted;
};

export const parseCurrency = (value) => {
    if (!value) return 0; // Return 0 if value is falsy
    return value.replace(/[^0-9.-]+/g, "");
};

// Function to generate a random color
const generateRandomColor = () => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
};

// Get the color for a team. If the team is not in the map, generate a new color.
export const getTeamColor = (teamName) => {

    const teamColorMap = {
        blue: '#2196f3',
        orange: '#ff9800',
        gold: '#ffc107',
        green: '#8bc34a',
        red: '#f44336',
        dtw: '#9c27b0',

        uscustomers: '#3f51b5',
        mxcustomers: '#ff5722',
        rxo: '#009688',
    };

    const lowerCaseTeamName = teamName.toLowerCase().replace("team", "").replace(" ", "");

    if (teamColorMap[lowerCaseTeamName]) {
        return teamColorMap[lowerCaseTeamName];
    } else {
        const newColor = stringToColor(lowerCaseTeamName);
        teamColorMap[lowerCaseTeamName] = newColor; // Add the new color to the map
        return newColor;
    }
};
